
import {
  CharacterClass,
  Helix2000Character
} from '../../shared/CharacterTypes';
import { calculateBaseStats, calculateHealthMana, createClassAbilities, createDefaultActionBar, createDefaultProfessions, getClassConfig } from './ClassUtils';
import { getJobAdvancement } from './JobAdvancements';
import { DEFAULT_KEY_BINDINGS } from './KeyBindings';
import { TALENT_TREES } from './TalentTrees';
import { starterHotbarSlots } from '../hotbar/starterHotbar';
import { PLAYER_HITBOX } from '../logic/CombatAnchors';

// Create platformer character with physics properties
export function createHelix2000Character(name: string, characterClass: CharacterClass, id?: string): Helix2000Character {
  const config = getClassConfig(characterClass);
  if (!config) {
    console.error(`[createHelix2000Character] Invalid character class: ${characterClass}`);
    throw new Error(`Invalid character class: ${characterClass}`);
  }
  const stats = calculateBaseStats(characterClass, 1);
  const { maxHealth, maxMana } = calculateHealthMana(characterClass, 1);
  const job = getJobAdvancement(characterClass, 1);

  const now = Date.now();
  const spawnX = 200;
  const spawnY = 300;
  const characterId = id || `char_${now}_${Math.random().toString(36).substr(2, 9)}`;

  const char: Helix2000Character = {
    // Basic character properties
    id: characterId,
    _id: characterId,
    name: name.trim(),
    class: characterClass,
    job: job,
    level: 1,
    xp: 0,
    skillPoints: 0,
    statPoints: 0,
    health: maxHealth,
    maxHealth: maxHealth,
    mana: maxMana,
    maxMana: maxMana,
    abilities: createClassAbilities(characterClass),
    talents: JSON.parse(JSON.stringify(TALENT_TREES[characterClass])),
    professions: createDefaultProfessions(),
    position: { x: spawnX, y: spawnY },
    // SSOT for the player's collision box. Combat reads the CENTRE of it
    // (`CombatAnchors.playerCombatPoint`) and the server — whose schema carries
    // no player dimensions — reads the same constant, so a size change here
    // moves both sides at once. See CombatAnchors.ts (COMBAT-MELEE-CONE-04).
    width: PLAYER_HITBOX.width,
    height: PLAYER_HITBOX.height,
    color: config.color,
    sprite: undefined,
    stats: stats,
    inventory: { items: [], gold: 0, maxSlots: 20 },
    quests: { active: [], completed: [] },
    created: now,
    lastPlayed: now,

    // Platformer-specific properties
    velocity: { x: 0, y: 0 },
    isGrounded: false,
    isJumping: false,
    jumpCount: 0,
    maxJumps: 2,
    facing: 'right',
    onPlatform: false,
    platformId: undefined,
    isMoving: false,
    movementStats: config.movementStats,

    // Combat State
    lastGCDTime: 0,
    attackSpeed: 1.0,
    weaponReach: 1.0,

    // New Systems
    actionBar: createDefaultActionBar(),
    keyBindings: JSON.parse(JSON.stringify(DEFAULT_KEY_BINDINGS)),
    trainingProgress: {},

    // [NEW] Rogue specific state
    starBurstCount: 0,
    isReloading: false
  };

  // Rogue starting inventory (only rogue needs this).
  if (characterClass === CharacterClass.ROGUE) {
    char.inventory.items.push({
      id: 'subi_throwing_star',
      name: 'Subi Throwing Star',
      type: 'material',
      quantity: 10000,
      maxStack: 10000,
      icon: '/icons/subi_throwing_star.svg',
      description: 'A basic throwing star used by assassins.'
    });
  }

  // Default action bar: the same starter layout the server creates and
  // repairs (starterHotbarSlots), so the bar keeps its slot count when the
  // server's hotbar first arrives.
  if (char.actionBar) {
    starterHotbarSlots(characterClass).forEach((entry, i) => {
      char.actionBar!.slots[i] = {
        slot: entry.slot,
        type: entry.type,
        abilityId: entry.abilityId,
        key: entry.key,
      };
    });
  }

  return char;
}
